// @ts-nocheck
import { useQueryClient, useMutation } from 'react-query';
import { useRequestQuery } from '../useQueryRequest';
import useApiRequest from '../useRequest';
import { transformPagination } from '@/utils';
import t from './types';
import { useRequestPdf } from '../useRequestPdf';

const commonInvalidateQueries = (queryClient) => {
  queryClient.invalidateQueries(t.SALE_PRESENTATIONS);
};

/**
 * Creates a new presentation.
 */
export function useCreatePresentation(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation((values) => apiRequest.post('sales/presentations', values), {
    onSuccess: () => {
      commonInvalidateQueries(queryClient);
      queryClient.invalidateQueries([t.SETTING, t.SETTING_PRESENTATIONS]);
    },
    ...props,
  });
}

/**
 * Edits the given presentation.
 */
export function useEditPresentation(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation(
    ([id, values]) => apiRequest.post(`sales/presentations/${id}`, values),
    {
      onSuccess: (res, [id]) => {
        commonInvalidateQueries(queryClient);
        queryClient.invalidateQueries([t.SALE_PRESENTATION, id]);
      },
      ...props,
    },
  );
}

/**
 * Deletes the given presentation.
 */
export function useDeletePresentation(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation((id) => apiRequest.delete(`sales/presentations/${id}`), {
    onSuccess: (res, id) => {
      commonInvalidateQueries(queryClient);
      queryClient.invalidateQueries([t.SALE_PRESENTATION, id]);
    },
    ...props,
  });
}

/**
 * Deletes a single item from a presentation.
 * Fixed: was using undefined bare `request` / `queryClient` variables.
 */
export function useDeletePresentationItem(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation(
    ({ presentationId, itemId }) =>
      apiRequest.delete(`sales/presentations/${presentationId}/items/${itemId}`),
    {
      onSuccess: (res, { presentationId }) => {
        queryClient.invalidateQueries([t.SALE_PRESENTATION, presentationId]);
        commonInvalidateQueries(queryClient);
      },
      ...props,
    },
  );
}

/**
 * Creates a presentation item on an existing presentation.
 */
export function useCreatePresentationItem(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation(
    ([presentationId, values]) =>
      apiRequest.post(`sales/presentations/${presentationId}/items`, values),
    {
      onSuccess: (res, [presentationId]) => {
        queryClient.invalidateQueries([t.SALE_PRESENTATION, presentationId]);
        commonInvalidateQueries(queryClient);
      },
      ...props,
    },
  );
}

/**
 * Edits a single presentation item.
 */
export function usePresentationItem(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation(
    ([presentationId, itemId, values]) =>
      apiRequest.put(`sales/presentations/${presentationId}/items/${itemId}`, values),
    {
      onSuccess: (res, [presentationId, itemId]) => {
        queryClient.invalidateQueries([t.SALE_PRESENTATION, presentationId]);
        queryClient.invalidateQueries([
          t.SALE_PRESENTATION,
          presentationId,
          'ITEM',
          itemId,
        ]);
        commonInvalidateQueries(queryClient);
      },
      ...props,
    },
  );
}

/**
 * Retrieve presentation details.
 */
export function usePresentation(id, props) {
  return useRequestQuery(
    [t.SALE_PRESENTATION, id],
    { method: 'get', url: `sales/presentations/${id}` },
    {
      select: (res) => res.data.presentation,
      defaultData: {},
      ...props,
    },
  );
}

const transformPresentations = (res) => ({
  presentations: res.data.sales_presentations || res.data.presentations,
  pagination: transformPagination(res.data.pagination),
  filterMeta: res.data.filter_meta,
});

/**
 * Retrieve presentations list with pagination meta.
 */
export function usePresentations(query, props) {
  return useRequestQuery(
    [t.SALE_PRESENTATIONS, query],
    { method: 'get', url: 'sales/presentations', params: query },
    {
      select: transformPresentations,
      defaultData: {
        presentations: [],
        pagination: { page: 1, pageSize: 20, total: 0 },
        filterMeta: {},
      },
      ...props,
    },
  );
}

/**
 * Fetches a single presentation item's details.
 */
export function usePresentationItemDetails(presentationId, itemId, props, requestProps) {
  return useRequestQuery(
    [t.SALE_PRESENTATION, presentationId, 'ITEM', itemId],
    {
      method: 'get',
      url: `sales/presentations/${presentationId}/items/${itemId}`,
      ...requestProps,
    },
    {
      select: (res) => res?.data ?? res,
      ...props,
    },
  );
}

/**
 * Retrieve the presentation PDF document.
 */
export function usePdfPresentation(presentationId) {
  return useRequestPdf({ url: `sales/presentations/${presentationId}` });
}

export function useRefreshPresentations() {
  const queryClient = useQueryClient();
  return {
    refresh: () => queryClient.invalidateQueries(t.SALE_PRESENTATIONS),
  };
}
